import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { shiftMonth } from './prima-nota-period';

type PrimaNotaHeaderProps = {
  period: string;
  /** An action is in flight: the month may not change under it. */
  actionRunning: boolean;
  onChange: (period: string) => void;
};

export function PrimaNotaHeader({ period, actionRunning, onChange }: PrimaNotaHeaderProps) {
  const { t } = useTranslation('finance');
  return (
    <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
      <div className="min-w-0 space-y-1">
        <h2 className="screen-title">{t('primaNota.title')}</h2>
        <p className="screen-summary">{t('primaNota.subtitle')}</p>
      </div>
      <div className="space-y-2">
        <label className="field-label" htmlFor="prima-nota-period">
          {t('primaNota.periodLabel')}
        </label>
        {/* Locked while an action is in flight: its answer, arriving after the month changed,
              would be re-read for, and shown under, the new month. Not while a month is merely
              being read: the newest read wins, and a month stepped through with the keyboard would
              lose focus at every step. The arrows are the same change, one month at a time. */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('primaNota.periodPrevious')}
            disabled={actionRunning || period === ''}
            onClick={() => onChange(shiftMonth(period, -1))}
          >
            <ChevronLeft aria-hidden="true" />
          </Button>
          <Input
            id="prima-nota-period"
            type="month"
            value={period}
            disabled={actionRunning}
            onChange={(event) => onChange(event.target.value)}
            className="w-48 font-medium"
          />
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('primaNota.periodNext')}
            disabled={actionRunning || period === ''}
            onClick={() => onChange(shiftMonth(period, 1))}
          >
            <ChevronRight aria-hidden="true" />
          </Button>
        </div>
        <p className="text-muted-foreground text-xs">{t('primaNota.periodHint')}</p>
      </div>
    </div>
  );
}
